import { useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MOCK_INVOICES, MOCK_DAY_RATES, MOCK_VESSELS, MOCK_EMISSIONS, resolveClientKey, getClientVessels, getClientInvoices } from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { DollarSign, Download, Search, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const statusColors: Record<string, string> = {
  paid: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  overdue: "bg-red-100 text-red-700",
};

const CommercialFinance = () => {
  const [invoiceFilter, setInvoiceFilter] = useState("all");
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientVessels = clientKey ? getClientVessels(clientKey) : MOCK_VESSELS;
  const clientInvoices = clientKey ? getClientInvoices(clientKey) : MOCK_INVOICES;
  const clientDayRates = clientKey ? MOCK_DAY_RATES.filter(d => clientVessels.some(v => v.name === d.vessel)) : MOCK_DAY_RATES;

  const totalInvoiced = clientInvoices.reduce((s, i) => s + i.amount, 0);
  const outstanding = clientInvoices.filter(i => i.status !== "paid").reduce((s, i) => s + i.amount, 0);
  const lastPayment = clientInvoices.find(i => i.status === "paid");

  const fuelData = MOCK_EMISSIONS.monthly.map(m => {
    const result: any = { month: m.month };
    clientVessels.slice(0, 3).forEach(v => {
      const key = v.name.replace("Velocity ", "").toLowerCase();
      result[key] = +((m as any)[key] * 0.31 || 0).toFixed(0);
    });
    return result;
  });

  const filteredInvoices = clientInvoices.filter(i => invoiceFilter === "all" || i.status === invoiceFilter);

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Commercial</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Commercial & Finance</h1>
      </div>

      <Tabs defaultValue="invoices" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="dayrate">Day Rate Ledger</TabsTrigger>
          <TabsTrigger value="fuel">Fuel Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="portal-card p-4"><p className="text-[10px] text-[hsl(215,12%,50%)] uppercase">Total Invoiced YTD</p><p className="text-2xl font-bold text-[hsl(215,25%,17%)]">${(totalInvoiced/1000).toFixed(0)}K</p></div>
            <div className="portal-card p-4"><p className="text-[10px] text-[hsl(215,12%,50%)] uppercase">Outstanding Balance</p><p className="text-2xl font-bold text-[#FFC107]">${(outstanding/1000).toFixed(0)}K</p></div>
            <div className="portal-card p-4"><p className="text-[10px] text-[hsl(215,12%,50%)] uppercase">Last Payment</p><p className="text-2xl font-bold text-[#28A745]">${lastPayment ? (lastPayment.amount/1000).toFixed(0) + "K" : "—"}</p></div>
          </div>
          <div className="portal-card">
            <div className="p-4 flex gap-3 border-b border-[hsl(220,15%,90%)]">
              <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent>
              </Select>
              <Button onClick={() => toast.success("Generating: Export", { description: "Your file will download shortly." })} variant="outline" size="sm" className="ml-auto"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Invoice No.", "Client", "Period", "Vessel", "Amount", "Status", "Due Date", ""].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filteredInvoices.map(inv => (
                    <tr key={inv.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                      <td className="px-4 py-2.5 font-medium">{inv.invoiceNo}</td>
                      <td className="px-4 py-2.5">{inv.client}</td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{inv.period}</td>
                      <td className="px-4 py-2.5">{inv.vessel}</td>
                      <td className="px-4 py-2.5 font-bold">${inv.amount.toLocaleString()}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${statusColors[inv.status]}`}>{inv.status}</span></td>
                      <td className="px-4 py-2.5">{inv.dueDate}</td>
                      <td className="px-4 py-2.5"><Button onClick={() => toast.success("Action recorded: Action")} variant="ghost" size="sm"><Download className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="dayrate">
          <div className="portal-card">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Date", "Vessel", "Event", "Daily Rate", "Billable", "Running Total"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {clientDayRates.map((r, i) => (
                    <tr key={i} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                      <td className="px-4 py-2.5">{r.date}</td>
                      <td className="px-4 py-2.5 font-medium">{r.vessel}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${r.event === "On Hire" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{r.event}</span></td>
                      <td className="px-4 py-2.5">${r.dailyRate.toLocaleString()}</td>
                      <td className="px-4 py-2.5 font-bold">${r.billable.toLocaleString()}</td>
                      <td className="px-4 py-2.5 font-bold text-[hsl(211,100%,40%)]">${r.runningTotal.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fuel">
          <div className="portal-card p-4">
            <h3 className="font-bold text-sm mb-3">Monthly Fuel Cost per Vessel (USD)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={fuelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#999" }} />
                <YAxis tick={{ fontSize: 10, fill: "#999" }} />
                <Tooltip />
                {clientVessels.slice(0, 3).map((v, i) => {
                  const key = v.name.replace("Velocity ", "").toLowerCase();
                  const colors = ["#0066CC", "#28A745", "#FFC107", "#17A2B8", "#DC3545"];
                  return <Bar key={key} dataKey={key} name={v.name.replace("Velocity ", "")} fill={colors[i % colors.length]} radius={[3,3,0,0]} />;
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default CommercialFinance;
